<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import { templateParametersComplete } from 'dashboard/helper/templateHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import SettingsLayout from '../SettingsLayout.vue';
import FlowCanvas from './FlowCanvas.vue';
import { buildConversationFlowTemplate } from './flowTemplates';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const flow = useMapGetter('conversationFlows/getConversationFlow');
const inboxes = useMapGetter('inboxes/getInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');
const flows = useMapGetter('conversationFlows/getConversationFlows');
const uiFlags = useMapGetter('conversationFlows/getUIFlags');
const canvasRef = ref(null);
const selectedInbox = ref(null);
const importedFlowDraft = ref(null);

const isNewFlow = computed(() => route.params.flowId === 'new');
const selectedTemplateId = computed(() => route.query.templateId || '');
const selectedImportToken = computed(() => route.query.importToken || '');

const templateDraftCatalog = computed(() => ({
  welcome_buttons: {
    name: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.WELCOME_BUTTONS.TITLE'),
    description: t(
      'CONVERSATION_FLOWS.TEMPLATES.ITEMS.WELCOME_BUTTONS.DESCRIPTION'
    ),
  },
  chatbot_buttons: {
    name: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_BUTTONS.TITLE'),
    description: t(
      'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_BUTTONS.DESCRIPTION'
    ),
  },
  chatbot_list: {
    name: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_LIST.TITLE'),
    description: t(
      'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CHATBOT_LIST.DESCRIPTION'
    ),
  },
  cards_carousel: {
    name: t('CONVERSATION_FLOWS.TEMPLATES.ITEMS.CARDS_CAROUSEL.TITLE'),
    description: t(
      'CONVERSATION_FLOWS.TEMPLATES.ITEMS.CARDS_CAROUSEL.DESCRIPTION'
    ),
  },
}));

const draftFlow = computed(() => {
  const templateId = selectedTemplateId.value;
  const templateMeta = templateDraftCatalog.value[templateId] || {};

  return {
    id: 'new',
    name:
      importedFlowDraft.value?.name ||
      templateMeta.name ||
      t('CONVERSATION_FLOWS.DEFAULT_NAME'),
    description:
      importedFlowDraft.value?.description ||
      templateMeta.description ||
      t('CONVERSATION_FLOWS.DEFAULT_DESCRIPTION'),
    channel_type: 'whatsapp',
    inbox_id: Number(route.query.inboxId) || selectedInbox.value?.id,
    status: 'draft',
    definition:
      importedFlowDraft.value?.definition ||
      (templateId
        ? buildConversationFlowTemplate(templateId, {
            noMatchTitle: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'),
          })
        : {
            nodes: [],
            edges: [],
          }),
  };
});

const editorFlow = computed(() => {
  return isNewFlow.value ? draftFlow.value : flow.value;
});

const flowTitle = computed(
  () => editorFlow.value?.name || t('CONVERSATION_FLOWS.EDITOR.TITLE')
);

const publishedFlows = computed(() =>
  flows.value.filter(
    publishedFlow =>
      publishedFlow.status === 'published' &&
      publishedFlow.id !== editorFlow.value?.id
  )
);

const selectedInboxTemplates = computed(() =>
  selectedInbox.value?.id
    ? getFilteredWhatsAppTemplates.value(selectedInbox.value.id) || []
    : []
);

const executableNodeTypes = [
  'text',
  'interactive',
  'image',
  'video',
  'audio',
  'location',
  'locationRequest',
  'contact',
  'carousel',
  'ai',
  'template',
  'media',
  'file',
];

const goBack = () => {
  router.push({ name: 'conversation_flows_index' });
};

const isWhatsAppInbox = inbox => {
  return (
    inbox.channel_type === INBOX_TYPES.WHATSAPP ||
    (inbox.channel_type === INBOX_TYPES.TWILIO &&
      inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP)
  );
};

const inboxOptions = computed(() =>
  inboxes.value
    .filter(inbox => isWhatsAppInbox(inbox))
    .map(inbox => ({
      id: inbox.id,
      name: inbox.name,
      icon: 'i-woot-whatsapp',
    }))
);

watch(
  [editorFlow, inboxOptions],
  () => {
    if (isNewFlow.value && !selectedInbox.value) {
      selectedInbox.value =
        inboxOptions.value.find(
          inbox => inbox.id === Number(route.query.inboxId)
        ) ||
        inboxOptions.value[0] ||
        null;
      return;
    }

    selectedInbox.value =
      inboxOptions.value.find(
        inbox => inbox.id === editorFlow.value.inbox_id
      ) || null;
  },
  { immediate: true }
);

const nodeById = definition => {
  const nodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  return Object.fromEntries(nodes.map(node => [node.id, node]));
};

const outgoingConnections = (definition, nodeId, outputKey = null) => {
  const connections = definition?.connections || definition?.edges || [];
  return connections.filter(connection => {
    if (connection.source !== nodeId) return false;
    if (!outputKey) return true;
    return connection.sourceOutput === outputKey;
  });
};

const hasOutgoingConnection = (definition, nodeId, outputKey = null) =>
  outgoingConnections(definition, nodeId, outputKey).length > 0;

const entryNodes = definition => {
  const nodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  return nodes.filter(node => ['start', 'noMatch'].includes(node.type));
};

const hasDuplicateNoMatchFlow = definition => {
  const nodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  const hasNoMatch = nodes.some(node => node.type === 'noMatch');
  if (!hasNoMatch || !selectedInbox.value?.id) return false;

  return flows.value.some(existingFlow => {
    if (existingFlow.id === editorFlow.value?.id) return false;
    if (existingFlow.inbox_id !== selectedInbox.value.id) return false;
    if (existingFlow.status === 'archived') return false;

    const existingNodes = Array.isArray(existingFlow.definition?.nodes)
      ? existingFlow.definition.nodes
      : [];
    return existingNodes.some(node => node.type === 'noMatch');
  });
};

const reachableNodeIds = definition => {
  const entryNode = entryNodes(definition)[0];
  if (!entryNode) return new Set();

  const visited = new Set();
  const stack = [entryNode.id];

  while (stack.length) {
    const nodeId = stack.pop();
    if (nodeId && !visited.has(nodeId)) {
      visited.add(nodeId);

      outgoingConnections(definition, nodeId).forEach(connection => {
        if (connection?.target && !visited.has(connection.target)) {
          stack.push(connection.target);
        }
      });
    }
  }

  return visited;
};

const disconnectedNode = definition => {
  const nodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  const reachableIds = reachableNodeIds(definition);

  return nodes.find(node => !reachableIds.has(node.id));
};

const validateDefinitionForSave = definition => {
  const availableEntryNodes = entryNodes(definition);

  if (availableEntryNodes.length !== 1) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ENTRY_REQUIRED');
  }

  if (hasDuplicateNoMatchFlow(definition)) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.NO_MATCH_UNIQUE');
  }

  const orphanNode = disconnectedNode(definition);
  if (orphanNode) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.DISCONNECTED_NODES');
  }

  return '';
};

const validLocationCoordinateRange = node => {
  const latitude = Number(node?.config?.latitude);
  const longitude = Number(node?.config?.longitude);

  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};

const validateDefinitionForPublish = definition => {
  const nodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  const nodesMap = nodeById(definition);
  const availableEntryNodes = entryNodes(definition);

  if (!selectedInbox.value?.id) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INBOX_REQUIRED');
  }

  if (availableEntryNodes.length !== 1) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ENTRY_REQUIRED');
  }

  const entryNode = availableEntryNodes[0];

  if (entryNode?.type === 'start' && !entryNode?.config?.title?.trim()) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.START_TITLE_REQUIRED');
  }

  if (entryNode?.type === 'noMatch' && hasDuplicateNoMatchFlow(definition)) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.NO_MATCH_UNIQUE');
  }

  const orphanNode = disconnectedNode(definition);
  if (orphanNode) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.DISCONNECTED_NODES');
  }

  if (!hasOutgoingConnection(definition, entryNode.id, 'next')) {
    return entryNode?.type === 'noMatch'
      ? t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.NO_MATCH_ROUTE_REQUIRED')
      : t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.START_ROUTE_REQUIRED');
  }

  const invalidNode = nodes.find(node => {
    if (node.type === 'text' && !node?.config?.body?.trim()) {
      return true;
    }

    if (
      node.type === 'image' &&
      !node?.config?.blobId &&
      !node?.config?.mediaLink?.trim()
    ) {
      return true;
    }

    if (
      node.type === 'video' &&
      !node?.config?.blobId &&
      !node?.config?.mediaLink?.trim()
    ) {
      return true;
    }

    if (
      node.type === 'audio' &&
      !node?.config?.blobId &&
      !node?.config?.mediaLink?.trim()
    ) {
      return true;
    }

    if (
      node.type === 'file' &&
      !node?.config?.blobId &&
      !node?.config?.mediaLink?.trim()
    ) {
      return true;
    }

    if (
      node.type === 'location' &&
      (node?.config?.latitude === '' ||
        node?.config?.latitude === null ||
        node?.config?.longitude === '' ||
        node?.config?.longitude === null ||
        !validLocationCoordinateRange(node))
    ) {
      return true;
    }

    if (node.type === 'locationRequest' && !node?.config?.body?.trim()) {
      return true;
    }

    if (node.type === 'carousel') {
      const cards = Array.isArray(node?.config?.cards) ? node.config.cards : [];

      if (!node?.config?.body?.trim()) {
        return true;
      }

      if (cards.length < 2 || cards.length > 10) {
        return true;
      }

      const hasInvalidCard = cards.some(card => {
        const hasMedia = card?.blobId || card?.mediaLink?.trim();
        if (!hasMedia) return true;

        if (node?.config?.actionType === 'url') {
          return !card?.buttonText?.trim() || !card?.buttonUrl?.trim();
        }

        if (node?.config?.actionType === 'quickReply') {
          const replies = Array.isArray(card?.quickReplies)
            ? card.quickReplies
            : [];
          return (
            !replies.length ||
            replies.length > 2 ||
            replies.some(reply => !reply?.title?.trim())
          );
        }

        return false;
      });

      if (hasInvalidCard) {
        return true;
      }
    }

    if (
      node.type === 'contact' &&
      (!node?.config?.phoneNumber?.trim() ||
        !node?.config?.formattedName?.trim())
    ) {
      return true;
    }

    if (node.type === 'template') {
      const template = selectedInboxTemplates.value.find(
        item => item.id === Number(node?.config?.templateId)
      );

      if (!template || !node?.config?.templateName) {
        return true;
      }

      return !templateParametersComplete(
        template,
        node?.config?.processedParams || {}
      );
    }

    if (node.type === 'interactive' && !node?.config?.body?.trim()) {
      return true;
    }

    if (node.type === 'button') {
      if (node?.config?.nextStepType === 'flow') {
        if (!node?.config?.linkedFlowId) {
          return true;
        }

        if (hasOutgoingConnection(definition, node.id, 'next')) {
          return true;
        }

        return false;
      }

      if (!node?.config?.title?.trim()) {
        return true;
      }

      if (!hasOutgoingConnection(definition, node.id, 'next')) {
        return true;
      }
    }

    if (node.type === 'row') {
      if (!node?.config?.title?.trim()) {
        return true;
      }

      if (!node?.config?.payload?.trim()) {
        return true;
      }

      if (!hasOutgoingConnection(definition, node.id, 'next')) {
        return true;
      }
    }

    if (node.type === 'interactive') {
      const buttonConnections = outgoingConnections(
        definition,
        node.id,
        'buttons'
      );
      const listConnections = outgoingConnections(definition, node.id, 'list');
      const hasMediaHeader =
        node?.config?.header?.enabled && node?.config?.header?.type === 'media';

      if (buttonConnections.length && listConnections.length) {
        return true;
      }

      if (hasMediaHeader && listConnections.length) {
        return true;
      }

      if (buttonConnections.length > 3) {
        return true;
      }
    }

    if (node.type === 'section') {
      const rowConnections = outgoingConnections(definition, node.id, 'rows');
      if (!rowConnections.length) {
        return true;
      }

      if (rowConnections.length > 10) {
        return true;
      }
    }

    if (['button', 'row'].includes(node.type)) {
      const targetNode = outgoingConnections(definition, node.id, 'next')
        .map(connection => nodesMap[connection.target])
        .find(Boolean);

      if (targetNode && !executableNodeTypes.includes(targetNode.type)) {
        return true;
      }
    }
    return false;
  });

  if (invalidNode?.type === 'text') {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEXT_BODY_REQUIRED');
  }

  if (invalidNode?.type === 'image') {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.IMAGE_MEDIA_REQUIRED');
  }

  if (invalidNode?.type === 'video') {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.VIDEO_MEDIA_REQUIRED');
  }

  if (invalidNode?.type === 'audio') {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.AUDIO_MEDIA_REQUIRED');
  }

  if (invalidNode?.type === 'file') {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.FILE_MEDIA_REQUIRED');
  }

  if (invalidNode?.type === 'location') {
    if (!validLocationCoordinateRange(invalidNode)) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LOCATION_COORDINATES_INVALID'
      );
    }

    return t(
      'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LOCATION_COORDINATES_REQUIRED'
    );
  }

  if (invalidNode?.type === 'locationRequest') {
    return t(
      'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LOCATION_REQUEST_BODY_REQUIRED'
    );
  }

  if (invalidNode?.type === 'carousel') {
    const cards = Array.isArray(invalidNode?.config?.cards)
      ? invalidNode.config.cards
      : [];

    if (!invalidNode?.config?.body?.trim()) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_BODY_REQUIRED'
      );
    }

    if (cards.length < 2) {
      return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_CARD_MIN');
    }

    if (cards.length > 10) {
      return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_CARD_MAX');
    }

    const missingMedia = cards.some(
      card => !card?.blobId && !card?.mediaLink?.trim()
    );
    if (missingMedia) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_MEDIA_REQUIRED'
      );
    }

    if (invalidNode?.config?.actionType === 'url') {
      const missingUrl = cards.some(
        card => !card?.buttonText?.trim() || !card?.buttonUrl?.trim()
      );
      if (missingUrl) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_URL_REQUIRED'
        );
      }
    }

    if (invalidNode?.config?.actionType === 'quickReply') {
      const invalidReplies = cards.some(card => {
        const replies = Array.isArray(card?.quickReplies)
          ? card.quickReplies
          : [];
        return (
          !replies.length ||
          replies.length > 2 ||
          replies.some(reply => !reply?.title?.trim())
        );
      });

      if (invalidReplies) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_QUICK_REPLY_REQUIRED'
        );
      }
    }
  }

  if (invalidNode?.type === 'contact') {
    return t(
      'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CONTACT_REQUIRED_FIELDS'
    );
  }

  if (invalidNode?.type === 'template') {
    if (
      !invalidNode?.config?.templateId ||
      !invalidNode?.config?.templateName
    ) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEMPLATE_SELECTION_REQUIRED'
      );
    }

    return t(
      'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEMPLATE_PARAMS_REQUIRED'
    );
  }

  if (invalidNode?.type === 'interactive') {
    const buttonConnections = outgoingConnections(
      definition,
      invalidNode.id,
      'buttons'
    );
    const listConnections = outgoingConnections(
      definition,
      invalidNode.id,
      'list'
    );
    const hasMediaHeader =
      invalidNode?.config?.header?.enabled &&
      invalidNode?.config?.header?.type === 'media';

    if (!invalidNode?.config?.body?.trim()) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_BODY_REQUIRED'
      );
    }
    if (buttonConnections.length && listConnections.length) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_MODE_CONFLICT'
      );
    }
    if (hasMediaHeader && listConnections.length) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_MEDIA_LIST_CONFLICT'
      );
    }
    if (buttonConnections.length > 3) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_BUTTON_LIMIT'
      );
    }
  }

  if (invalidNode?.type === 'button') {
    if (invalidNode?.config?.nextStepType === 'flow') {
      if (!invalidNode?.config?.linkedFlowId) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_FLOW_REQUIRED'
        );
      }

      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_FLOW_ROUTE_CONFLICT'
      );
    }

    if (!invalidNode?.config?.title?.trim()) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_TITLE_REQUIRED'
      );
    }

    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_ROUTE_REQUIRED');
  }

  if (invalidNode?.type === 'row') {
    if (!invalidNode?.config?.title?.trim()) {
      return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_TITLE_REQUIRED');
    }
    if (!invalidNode?.config?.payload?.trim()) {
      return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_ID_REQUIRED');
    }

    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_ROUTE_REQUIRED');
  }

  if (invalidNode?.type === 'section') {
    const rowConnections = outgoingConnections(
      definition,
      invalidNode.id,
      'rows'
    );
    return rowConnections.length > 10
      ? t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.SECTION_ROWS_LIMIT')
      : t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.SECTION_ROWS_REQUIRED');
  }

  if (invalidNode && ['button', 'row'].includes(invalidNode.type)) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROUTING_TARGET_INVALID');
  }

  return '';
};

const persistFlow = async status => {
  const definition = canvasRef.value?.getDefinition();
  const flowParams = {
    name: canvasRef.value?.getFlowName(),
    description: editorFlow.value.description,
    channel_type: 'whatsapp',
    inbox_id: selectedInbox.value?.id,
    status,
    definition,
  };

  try {
    if (isNewFlow.value) {
      const createdFlow = await store.dispatch(
        'conversationFlows/create',
        flowParams
      );
      await router.replace({
        name: 'conversation_flow_editor',
        params: { flowId: createdFlow.id },
      });
      await store.dispatch('conversationFlows/show', createdFlow.id);
    } else {
      await store.dispatch('conversationFlows/update', {
        id: editorFlow.value.id,
        ...flowParams,
      });
    }
    useAlert(
      status === 'published'
        ? t('CONVERSATION_FLOWS.EDITOR.PUBLISH_SUCCESS')
        : t('CONVERSATION_FLOWS.EDITOR.SAVE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      status === 'published'
        ? t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERROR')
        : t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR')
    );
  }
};

const saveFlow = async () => {
  const validationError = validateDefinitionForSave(
    canvasRef.value?.getDefinition()
  );

  if (validationError) {
    useAlert(validationError);
    return;
  }

  await persistFlow('draft');
};

const publishFlow = async () => {
  const validationError = validateDefinitionForPublish(
    canvasRef.value?.getDefinition()
  );

  if (validationError) {
    useAlert(validationError);
    return;
  }

  await persistFlow('published');
};

watch(
  () => selectedImportToken.value,
  importToken => {
    if (!isNewFlow.value || !importToken) {
      importedFlowDraft.value = null;
      return;
    }

    try {
      const storedDraft = sessionStorage.getItem(importToken);
      importedFlowDraft.value = storedDraft ? JSON.parse(storedDraft) : null;
      sessionStorage.removeItem(importToken);
    } catch (error) {
      importedFlowDraft.value = null;
    }
  },
  { immediate: true }
);

watch(
  () => route.params.flowId,
  flowId => {
    if (!flowId || flowId === 'new') return;

    store.dispatch('conversationFlows/show', flowId);
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('conversationFlows/get');
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="!isNewFlow && uiFlags.isFetching"
    :loading-message="$t('CONVERSATION_FLOWS.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <div class="flex items-center justify-between gap-4 min-h-10">
        <div class="flex items-center min-w-0 gap-3">
          <Button icon="i-lucide-arrow-left" slate sm @click="goBack" />
          <div class="min-w-0">
            <h1 class="m-0 text-xl font-semibold text-n-slate-12 truncate">
              {{ flowTitle }}
            </h1>
            <p class="mt-1 mb-0 text-sm text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="flex items-center flex-shrink-0 gap-2">
          <span
            class="text-xs text-n-slate-12 bg-n-slate-4 rounded-md py-0.5 px-1.5 capitalize"
          >
            {{ editorFlow.status }}
          </span>
          <Button
            :label="$t('CONVERSATION_FLOWS.EDITOR.SAVE_DRAFT')"
            icon="i-lucide-save"
            :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
            @click="saveFlow"
          />
          <Button
            :label="$t('CONVERSATION_FLOWS.EDITOR.PUBLISH')"
            icon="i-lucide-send"
            :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
            @click="publishFlow"
          />
        </div>
      </div>
      <div class="flex justify-end pt-4">
        <div class="w-full lg:w-80">
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('CONVERSATION_FLOWS.EDITOR.CHANNEL_SELECT') }}
          </p>
          <SingleSelect
            v-model="selectedInbox"
            :options="inboxOptions"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.CHANNEL_PLACEHOLDER')"
            placeholder-icon="i-woot-whatsapp"
            :search-placeholder="$t('CONVERSATION_FLOWS.EDITOR.CHANNEL_SEARCH')"
            disable-deselect
          />
        </div>
      </div>
    </template>

    <template #body>
      <section
        dir="ltr"
        class="flex flex-col mt-4 overflow-hidden border rounded-lg border-n-weak bg-n-alpha-1 min-h-[calc(100vh-10rem)]"
      >
        <FlowCanvas
          v-if="editorFlow.id"
          :key="editorFlow.id"
          ref="canvasRef"
          :definition="editorFlow.definition"
          :inbox-id="selectedInbox?.id"
          :agents="agents"
          :labels="labels"
          :published-flows="publishedFlows"
          :whats-app-templates="selectedInboxTemplates"
        />
      </section>
    </template>
  </SettingsLayout>
</template>
